import {
  infiniteQueryOptions,
  queryOptions,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';

import { api, extractData } from '@/lib/treaty';

export type SearchAllQueryFilters = Parameters<typeof api.search.get>;
type SearchAllInfiniteQueryFilter = SearchAllQueryFilters[0] & {
  query: NonNullable<SearchAllQueryFilters[0]['query']> & {
    searchType: 'all';
    pageSize: number;
  };
};

export const getSearchAll = (...args: SearchAllQueryFilters) => {
  return extractData(api.search.get(...args));
};

export const searchAllQueryKey = ['search', 'all'] as const;

export type SearchAllResult = Extract<
  Awaited<ReturnType<typeof getSearchAll>>,
  { estates: unknown[]; projects: unknown[] }
>;

const isSearchAllResult = (
  data: Awaited<ReturnType<typeof getSearchAll>>
): data is SearchAllResult =>
  !Array.isArray(data) &&
  typeof data === 'object' &&
  data !== null &&
  'estates' in data &&
  'projects' in data;

const getSearchAllPage = async (args: SearchAllInfiniteQueryFilter): Promise<SearchAllResult> => {
  const data = await getSearchAll(args);

  if (isSearchAllResult(data)) {
    return data;
  }

  throw new Error('Unexpected search response for searchType "all"');
};

export const getSearchAllQueryOptions = (...args: SearchAllQueryFilters) =>
  queryOptions({
    queryKey: [...searchAllQueryKey, ...args],
    queryFn: () => getSearchAll(...args),
    placeholderData: (previousData) => previousData,
  });

export const useSearchAll = (...args: SearchAllQueryFilters) => {
  return useQuery({ ...getSearchAllQueryOptions(...args) });
};

const getSearchAllInfiniteQueryKey = (args: SearchAllInfiniteQueryFilter) => [
  ...searchAllQueryKey,
  'infinite',
  {
    ...args,
    query: {
      searchType: args.query.searchType,
      pageSize: args.query.pageSize,
      filters: args.query.filters,
      sort: args.query.sort,
    },
  },
];

export const getSearchAllInfiniteQueryOptions = (args: SearchAllInfiniteQueryFilter) =>
  infiniteQueryOptions({
    queryKey: getSearchAllInfiniteQueryKey(args),
    queryFn: ({ pageParam }) =>
      getSearchAllPage({
        ...args,
        query: {
          ...args.query,
          page: pageParam,
        },
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _, lastPageParam) => {
      const hasFullEstatesPage = lastPage.estates.length >= args.query.pageSize;
      const hasFullProjectsPage = lastPage.projects.length >= args.query.pageSize;

      return hasFullEstatesPage || hasFullProjectsPage ? lastPageParam + 1 : undefined;
    },
  });

export const useSearchAllInfinite = (args: SearchAllInfiniteQueryFilter) => {
  return useInfiniteQuery({ ...getSearchAllInfiniteQueryOptions(args) });
};
